import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  TablePagination,
} from '@mui/material';
import {
  Send,
  Visibility,
  Delete,
  FilterList,
  Email,
  WhatsApp,
  Notifications,
  CheckCircle,
  Cancel,
  Schedule,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { backofficeApi } from '../../services/backofficeApi';

interface NotificationLog {
  id: string;
  notification_type: string;
  channel: string;
  recipient: string;
  status: string;
  sent_at: string;
  error_message?: string;
  template_id?: string;
  data: Record<string, any>;
}

interface NotificationStats {
  total_sent: number;
  total_delivered: number;
  total_failed: number;
  total_pending: number;
}

const NOTIFICATION_TYPES = [
  'USER_REGISTRATION',
  'USER_SIGNUP_REQUEST',
  'USER_SIGNUP_APPROVED',
  'USER_SIGNUP_REJECTED',
  'USER_PASSWORD_CHANGED',
  'USER_PROMOTIONAL',
  'ORDER_PLACED',
  'ORDER_CONFIRMED',
  'ORDER_SHIPPED',
  'ORDER_DELIVERED',
  'ORDER_INVOICED',
  'ORDER_CLOSED',
  'ORDER_CANCELLED',
  'PAYMENT_RECEIVED',
  'PAYMENT_REJECTED',
  'PAYMENT_REMINDER',
  'BACKOFFICE_ORDER_PLACED',
];

const CHANNELS = ['EMAIL', 'WHATSAPP', 'IN_APP'];
const STATUSES = ['sent', 'delivered', 'failed', 'pending'];

const NotificationManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog states
  const [viewingLog, setViewingLog] = useState<NotificationLog | null>(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    notification_type: '',
    channel: '',
    status: '',
    recipient: '',
    start_date: null as any,
    end_date: null as any,
  });

  // Send form
  const [sendForm, setSendForm] = useState({
    notification_type: '',
    channel: '',
    recipient: '',
    template_data: '{}',
  });

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [page, rowsPerPage, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params: any = {
        page: page + 1,
        size: rowsPerPage,
      };

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '') {
          if (key === 'start_date' || key === 'end_date') {
            params[key] = dayjs(value).format('YYYY-MM-DD');
          } else {
            params[key] = value;
          }
        }
      });

      const response = await backofficeApi.get('/backoffice/notification-management/logs', { params });
      setLogs(response.data.logs);
      setTotalCount(response.data.total_count);
    } catch (error: any) {
      setError('Failed to load logs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await backofficeApi.get('/backoffice/notification-management/stats');
      setStats(response.data);
    } catch (error: any) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSendNotification = async () => {
    try {
      setError('');
      const data = {
        ...sendForm,
        template_data: JSON.parse(sendForm.template_data),
      };

      await backofficeApi.post('/backoffice/notification-management/send', data);
      setSuccess('Notification sent successfully');
      setSendDialogOpen(false);
      loadLogs();
      loadStats();
    } catch (error: any) {
      setError('Failed to send notification: ' + error.message);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle color="success" />;
      case 'failed':
        return <Cancel color="error" />;
      case 'pending':
        return <Schedule color="warning" />;
      default:
        return <Send color="primary" />;
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Email color="primary" />;
      case 'whatsapp':
        return <WhatsApp color="success" />;
      case 'in_app':
        return <Notifications color="secondary" />;
      default:
        return <Notifications />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Notification Management
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Monitor and manage notification logs and statistics
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => setSendDialogOpen(true)}
          >
            Send Notification
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        {stats && (
          <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Total Sent
                      </Typography>
                      <Typography variant="h4" component="div">
                        {stats.total_sent.toLocaleString()}
                      </Typography>
                    </Box>
                    <Send sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Delivered
                      </Typography>
                      <Typography variant="h4" component="div">
                        {stats.total_delivered.toLocaleString()}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Failed
                      </Typography>
                      <Typography variant="h4" component="div">
                        {stats.total_failed.toLocaleString()}
                      </Typography>
                    </Box>
                    <Cancel sx={{ fontSize: 40, color: 'error.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Pending
                      </Typography>
                      <Typography variant="h4" component="div">
                        {stats.total_pending.toLocaleString()}
                      </Typography>
                    </Box>
                    <Schedule sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.notification_type}
                onChange={(e) => setFilters({ ...filters, notification_type: e.target.value })}
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {NOTIFICATION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Channel</InputLabel>
              <Select
                value={filters.channel}
                onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
                label="Channel"
              >
                <MenuItem value="">All Channels</MenuItem>
                {CHANNELS.map((channel) => (
                  <MenuItem key={channel} value={channel}>
                    {channel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                {STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Recipient"
              value={filters.recipient}
              onChange={(e) => setFilters({ ...filters, recipient: e.target.value })}
              sx={{ minWidth: 200 }}
            />

            <DatePicker
              label="Start Date"
              value={filters.start_date}
              onChange={(newValue) => setFilters({ ...filters, start_date: newValue })}
              slotProps={{ textField: { size: 'small' } }}
            />

            <DatePicker
              label="End Date"
              value={filters.end_date}
              onChange={(newValue) => setFilters({ ...filters, end_date: newValue })}
              slotProps={{ textField: { size: 'small' } }}
            />

            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={loadLogs}
            >
              Apply
            </Button>
          </Box>
        </Paper>

        {/* Logs Table */}
        <Paper>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {log.notification_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getChannelIcon(log.channel)}
                          <Typography variant="body2">
                            {log.channel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{log.recipient}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusIcon(log.status)}
                          <Chip
                            label={log.status.replace(/\b\w/g, l => l.toUpperCase())}
                            color={getStatusColor(log.status) as any}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(log.sent_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => setViewingLog(log)} size="small">
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              />
            </>
          )}
        </Paper>

        {/* View Log Dialog */}
        <Dialog open={!!viewingLog} onClose={() => setViewingLog(null)} maxWidth="md" fullWidth>
          <DialogTitle>Notification Log Details</DialogTitle>
          <DialogContent>
            {viewingLog && (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="subtitle2" color="textSecondary">Type:</Typography>
                    <Typography>{viewingLog.notification_type.replace(/_/g, ' ')}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="subtitle2" color="textSecondary">Channel:</Typography>
                    <Typography>{viewingLog.channel}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="subtitle2" color="textSecondary">Recipient:</Typography>
                    <Typography>{viewingLog.recipient}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="subtitle2" color="textSecondary">Status:</Typography>
                    <Chip
                      label={viewingLog.status}
                      color={getStatusColor(viewingLog.status) as any}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="subtitle2" color="textSecondary">Sent At:</Typography>
                    <Typography>{new Date(viewingLog.sent_at).toLocaleString()}</Typography>
                  </Box>
                </Box>

                {viewingLog.error_message && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="error">Error Message:</Typography>
                    <Paper sx={{ p: 2, backgroundColor: 'error.light', color: 'error.contrastText', mt: 1 }}>
                      <Typography variant="body2">{viewingLog.error_message}</Typography>
                    </Paper>
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Template Data:</Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50', mt: 1 }}>
                    <pre>{JSON.stringify(viewingLog.data, null, 2)}</pre>
                  </Paper>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewingLog(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Send Notification Dialog */}
        <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Send Manual Notification</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Notification Type</InputLabel>
                <Select
                  value={sendForm.notification_type}
                  onChange={(e) => setSendForm({ ...sendForm, notification_type: e.target.value })}
                  label="Notification Type"
                >
                  {NOTIFICATION_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={sendForm.channel}
                  onChange={(e) => setSendForm({ ...sendForm, channel: e.target.value })}
                  label="Channel"
                >
                  {CHANNELS.map((channel) => (
                    <MenuItem key={channel} value={channel}>
                      {channel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Recipient"
                value={sendForm.recipient}
                onChange={(e) => setSendForm({ ...sendForm, recipient: e.target.value })}
                placeholder="Email address, phone number, or user ID"
              />

              <TextField
                fullWidth
                label="Template Data (JSON)"
                value={sendForm.template_data}
                onChange={(e) => setSendForm({ ...sendForm, template_data: e.target.value })}
                multiline
                rows={4}
                placeholder='{"customer_name": "John Doe", "order_id": "12345"}'
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendNotification} variant="contained" startIcon={<Send />}>
              Send
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default NotificationManagementPage;